<template>
  <div class="plan-mobile-container">
    <!-- 操作按钮区域，固定在右下角 -->
    <div class="mobile-action-buttons">
      <!-- 查询按钮 -->
      <el-button class="mobile-icon-btn" @click="showFilterDialog">
        <i class="el-icon-search"></i>
      </el-button>
      
      <!-- 统计按钮 -->
      <el-button class="mobile-icon-btn" @click="showStatisticsDialog">
        <i class="el-icon-data-analysis"></i>
      </el-button>
    </div>
    
    <!-- 标题栏 -->
    <div class="mobile-header" :class="{ 'header-hidden': !headerVisible }">
      <h1 class="page-title">计划列表</h1>
    </div>
    
    <!-- 计划卡片区域 -->
    <div class="plan-cards-container" :class="{ 'header-hidden': !headerVisible }" v-loading="loading" @scroll="handleScroll">
      <div v-if="planList.length === 0 && !loading" class="no-data">
        <div class="no-data-content">
          <i class="el-icon-document"></i>
          <span class="no-data-title">暂无计划</span>
          <p class="no-data-tips">
            没有找到符合条件的计划，请尝试调整筛选条件
          </p>
        </div>
      </div>
      
      <template v-else>
        <div 
          v-for="plan in planList" 
          :key="plan.plan_code"
          class="plan-card"
          @click="handleCardClick(plan)"
          @touchstart="onTouchStart($event, plan)"
          @touchmove="onTouchMove($event, plan)"
          @touchend="onTouchEnd($event, plan)"
        >
          <!-- 状态印章 -->
          <div class="status-stamp">
            <div class="stamp-content" :class="getStatusStampClass(plan.plan_status)">
              {{ getStatusStampText(plan.plan_status) }}
            </div>
          </div>

          <!-- 滑动显示操作按钮 -->
          <div class="swipe-actions" :class="{ 'show': plan._showSwipeActions }">
            <div class="action-buttons">
              <el-button class="action-icon-btn" :disabled="!canStart(plan) || actionLoading" @click.stop="handleStart(plan)" title="开始">
                <i class="el-icon-video-play"></i>
              </el-button>
              <el-button class="action-icon-btn" :disabled="!canPause(plan) || actionLoading" @click.stop="handlePause(plan)" title="暂停">
                <i class="el-icon-video-pause"></i>
              </el-button>
              <el-button class="action-icon-btn" :disabled="!canComplete(plan) || actionLoading" @click.stop="handleComplete(plan)" title="完成">
                <i class="el-icon-check"></i>
              </el-button>
              <el-button class="action-icon-btn" :disabled="!canTerminate(plan) || actionLoading" @click.stop="handleTerminate(plan)" title="终止">
                <i class="el-icon-circle-close"></i>
              </el-button>
            </div>
          </div>
          
          <!-- 计划基本信息 -->
          <div class="plan-card-main">
            <!-- 计划名称区域 -->
            <div class="plan-header">
              <h3 class="plan-name">
                <span v-if="formatDurationDays(plan) !== '--'" class="duration-tag">{{ formatDurationDays(plan) }}</span>
                <span class="plan-name-text">{{ plan.plan_name }}</span>
              </h3>
            </div>
            
            <div class="plan-info">
              <!-- 来源题目 -->
              <div class="plan-question" v-if="plan.question_name">
                <span class="question-label">题目：</span>
                <a v-if="plan.question_url" 
                   :href="plan.question_url" 
                   target="_blank" 
                   class="question-value question-link"
                   @click.stop>
                  {{ plan.question_name }}
                </a>
                <span v-else class="question-value">{{ plan.question_name }}</span>
              </div>
              
              <!-- 计划来源和添加时间 -->
              <div class="plan-source-time">
                <span class="source-label">来源：</span>
                <span class="source-value" :class="plan.plan_from === 1 ? 'manual-add' : 'review-generate'">
                  {{ plan.plan_from === 1 ? '手动添加' : '复盘生成' }}@{{ formatDate(plan.add_time) }}
                </span>
              </div>
              
              <!-- 预计开始和预计完成时间 -->
              <div class="plan-time-grid">
                <div class="time-item">
                  <span class="time-label">预计开始：</span>
                  <span class="time-value">{{ formatDate(plan.scheduled_start_time) }}</span>
                </div>
                <div class="time-item">
                  <span class="time-label">预计完成：</span>
                  <span class="time-value">{{ formatDate(plan.scheduled_end_time) }}</span>
                </div>
              </div>
              
              <!-- 实际开始和实际完成时间 -->
              <div class="plan-time-grid" v-if="plan.start_time || plan.end_time">
                <div class="time-item" v-if="plan.start_time">
                  <span class="time-label">实际开始：</span>
                  <span class="time-value">{{ formatDate(plan.start_time) }}</span>
                </div>
                <div class="time-item" v-if="plan.end_time">
                  <span class="time-label">实际完成：</span>
                  <span class="time-value">{{ formatDate(plan.end_time) }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>
      
      <!-- 加载更多 -->
      <div v-if="hasMore && !loading && planList.length > 0" class="load-more">
        <el-button type="text" @click="loadMore">加载更多</el-button>
      </div>
      <div v-if="loading && planList.length > 0" class="loading-more">
        <i class="el-icon-loading"></i>
        <span>加载中...</span>
      </div>
      <div v-if="!hasMore && planList.length > 0" class="no-more">
        <span>------已经到底啦------</span>
        <div class="copyright-info">
          <span class="mobile-copyright">Copyright © 2024 OIer之路 All rights reserved.<br>闽ICP备2025095302号-1</span>
        </div>
      </div>
    </div>
    
    <!-- 统计信息弹窗 -->
    <div class="dialog-container" v-show="statisticsDialogVisible">
      <div class="dialog-mask" @click="statisticsDialogVisible = false"></div>
      <div class="custom-dialog stats-dialog" style="top: 10vh;">
        <div class="dialog-close" @click="statisticsDialogVisible = false">×</div>
        <div class="dialog-body statistics-dialog-body">
          <div class="dialog-content" style="background-color: #ffffff;">
            <h3 class="dialog-custom-title">计划统计</h3>
            <div class="mobile-statistics-grid">
              <!-- 未开始 -->
              <div class="stat-item">
                <div class="stat-value">{{ formatNumber(stats.not_started) }}</div>
                <div class="stat-label">
                  <i class="el-icon-time"></i>
                  未开始
                </div>
              </div>
              
              <!-- 进行中 -->
              <div class="stat-item">
                <div class="stat-value">{{ formatNumber(stats.in_progress) }}</div>
                <div class="stat-label">
                  <i class="el-icon-video-play"></i>
                  进行中
                </div>
              </div>
              
              <!-- 已完成 -->
              <div class="stat-item">
                <div class="stat-value">{{ formatNumber(stats.completed) }}</div>
                <div class="stat-label">
                  <i class="el-icon-check"></i>
                  已完成
                </div>
              </div>
              
              <!-- 已暂停 -->
              <div class="stat-item">
                <div class="stat-value">{{ formatNumber(stats.paused || 0) }}</div>
                <div class="stat-label">
                  <i class="el-icon-video-pause"></i>
                  已暂停
                </div>
              </div>
              
              <!-- 已终止 -->
              <div class="stat-item">
                <div class="stat-value">{{ formatNumber(stats.terminated) }}</div>
                <div class="stat-label">
                  <i class="el-icon-circle-close"></i>
                  已终止
                </div>
              </div>
              
              <!-- 总数 -->
              <div class="stat-item">
                <div class="stat-value">{{ formatNumber(stats.total) }}</div>
                <div class="stat-label">
                  <i class="el-icon-document"></i>
                  总数
                </div>
              </div>
              

              
              <!-- 完成率 -->
              <div class="stat-item">
                <div class="stat-value">{{ formatPercentage(stats.completion_rate || 0) }}</div>
                <div class="stat-label">
                  <i class="el-icon-data-line"></i>
                  完成率
                </div>
              </div>
              
              <!-- 进行率 -->
              <div class="stat-item">
                <div class="stat-value">{{ formatPercentage(stats.progress_rate || 0) }}</div>
                <div class="stat-label">
                  <i class="el-icon-sort"></i>
                  进行率
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <!-- 查询筛选弹窗 -->
    <div class="dialog-container" v-show="filterDialogVisible">
      <div class="dialog-mask" @click="filterDialogVisible = false"></div>
      <div class="custom-dialog" style="top: 10vh;">
        <div class="dialog-close" @click="filterDialogVisible = false">×</div>
        <div class="dialog-body filter-dialog-body">
          <div class="dialog-content filter-dialog-content" style="background-color: #ffffff;">
            <h3 class="dialog-custom-title filter-title">查询条件</h3>
            <el-form :model="filterForm" class="filter-form" size="small" label-width="70px">
              <el-form-item label="计划名称">
                <el-input v-model="filterForm.plan_name" placeholder="请输入计划名称"></el-input>
              </el-form-item>
              
              <el-form-item label="计划内容">
                <el-input v-model="filterForm.plan_info" placeholder="请输入计划内容"></el-input>
              </el-form-item>
              
              <el-form-item label="计划状态">
                <el-select v-model="filterForm.plan_status" placeholder="请选择计划状态" clearable style="width: 100%">
                  <el-option label="未开始" :value="0"></el-option>
                  <el-option label="进行中" :value="1"></el-option>
                  <el-option label="已完成" :value="2"></el-option>
                  <el-option label="暂停" :value="3"></el-option>
                  <el-option label="终止" :value="4"></el-option>
                </el-select>
              </el-form-item>
              
              <el-form-item label="添加时间">
                <div class="date-range-container">
                  <el-date-picker
                    v-model="filterForm.add_time"
                    type="daterange"
                    range-separator="至"
                    start-placeholder="开始日期"
                    end-placeholder="结束日期"
                    value-format="yyyy-MM-dd"
                    size="small"
                    style="width: 100%;"
                    class="date-picker-item">
                  </el-date-picker>
                </div>
              </el-form-item>
            </el-form>
            <div class="dialog-footer filter-footer">
              <el-button size="small" @click="handleReset">重置</el-button>
              <el-button size="small" type="primary" @click="handleSearch">查询</el-button>
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <!-- 计划详情弹窗 -->
    <div class="dialog-container" v-show="detailDialogVisible">
      <div class="dialog-mask" @click="detailDialogVisible = false"></div>
      <div class="custom-dialog detail-dialog" style="top: 10vh;">
        <div class="dialog-close" @click="detailDialogVisible = false">×</div>
        <div class="dialog-body">
          <div v-if="currentPlan" class="plan-detail" style="background-color: #ffffff;">
            <!-- 可滚动的内容区域 -->
            <div class="detail-scrollable-content">
              <div class="detail-header">
                <h3>{{ currentPlan.plan_name }}</h3>
                
                <!-- 状态印章 -->
                <div class="detail-status">
                  <div class="stamp-content" :class="getStatusStampClass(currentPlan.plan_status)">
                    {{ getStatusStampText(currentPlan.plan_status) }}
                  </div>
                </div>
              </div>
              
              <div class="detail-content">
                <div class="detail-item">
                  <div class="detail-label">计划来源</div>
                  <div class="detail-value">
                    <el-tag
                      :type="currentPlan.plan_from === 1 ? 'info' : 'warning'"
                      size="small">
                      {{ currentPlan.plan_from === 1 ? '手动添加' : '复盘生成' }}
                    </el-tag>
                  </div>
                </div>
                
                <div class="detail-item" v-if="currentPlan.question_name">
                  <div class="detail-label">来源题目</div>
                  <div class="detail-value">
                    <a v-if="currentPlan.question_url" 
                       :href="currentPlan.question_url" 
                       target="_blank" 
                       class="question-link">
                      {{ currentPlan.question_name }}
                    </a>
                    <span v-else>{{ currentPlan.question_name }}</span>
                  </div>
                </div>
                
                <div class="detail-item">
                  <div class="detail-label">添加时间</div>
                  <div class="detail-value">{{ formatDate(currentPlan.add_time) }}</div>
                </div>
                
                <div class="detail-item">
                  <div class="detail-label">预计时间</div>
                  <div class="detail-value time-range">
                    {{ formatDate(currentPlan.scheduled_start_time) }}
                    <span class="time-separator">-</span>
                    {{ formatDate(currentPlan.scheduled_end_time) }}
                  </div>
                </div>

                <div class="detail-item">
                  <div class="detail-label">实际时间</div>
                  <div class="detail-value time-range">
                    {{ formatDate(currentPlan.start_time) }}
                    <span class="time-separator">-</span>
                    {{ formatDate(currentPlan.end_time) }}
                  </div>
                </div>

                <div class="detail-item">
                  <div class="detail-label">耗时时间</div>
                  <div class="detail-value">
                    <span v-if="formatDurationDays(currentPlan) !== '--'" class="duration-tag">{{ formatDurationDays(currentPlan) }}</span>
                    <span v-else>--</span>
                  </div>
                </div>
                
                <div class="detail-section" v-if="currentPlan.plan_info">
                  <h4>计划内容</h4>
                  <div class="plan-info-content markdown-body" v-html="renderedPlanInfo"></div>
                </div>
              </div>
            </div>
            
            <!-- 固定的分割线和按钮区域 -->
            <div class="detail-actions-fixed">
              <div class="detail-actions">
                <el-button class="action-icon-btn" :disabled="!canStart(currentPlan) || actionLoading" @click="handleStart(currentPlan)" title="开始">
                  <i class="el-icon-video-play"></i>
                </el-button>
                <el-button class="action-icon-btn" :disabled="!canPause(currentPlan) || actionLoading" @click="handlePause(currentPlan)" title="暂停">
                  <i class="el-icon-video-pause"></i>
                </el-button>
                <el-button class="action-icon-btn" :disabled="!canComplete(currentPlan) || actionLoading" @click="handleComplete(currentPlan)" title="完成">
                  <i class="el-icon-check"></i>
                </el-button>
                <el-button class="action-icon-btn" :disabled="!canTerminate(currentPlan) || actionLoading" @click="handleTerminate(currentPlan)" title="终止">
                  <i class="el-icon-circle-close"></i>
                </el-button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getPlanList, getPlanStats, updatePlanStatus } from '@/api/plan'
import { marked } from 'marked'
import DOMPurify from 'dompurify'
import katex from 'katex'
import 'katex/dist/katex.min.css'

export default {
  name: 'PlanMobile',
  data() {
    return {
      loading: false,
      planList: [],
      currentPage: 1,
      pageSize: 20,
      total: 0,
      hasMore: true,
      sortProp: 'add_time',
      sortOrder: 'descending',
      
             // 统计数据
       stats: {
         not_started: 0,
         in_progress: 0,
         completed: 0,
         paused: 0,
         terminated: 0,
         total: 0,
         completion_rate: 0,
         progress_rate: 0
       },
      
      // 弹窗控制
      statisticsDialogVisible: false,
      filterDialogVisible: false,
      detailDialogVisible: false,
      
      // 当前选中的计划
      currentPlan: null,
      
      // 查询表单
      filterForm: {
        plan_name: '',
        plan_info: '',
        plan_status: '',
        add_time: []
      },

      // 触摸滑动状态
      touchStartX: 0,
      touchStartY: 0,
      isTouchMoving: false,
      ignoreEdgeSwipe: false,

      // 操作中的loading，避免重复点击
      actionLoading: false,

      // 滚动状态控制
      lastScrollTop: 0,
      headerVisible: true,
      scrollThreshold: 10 // 滚动阈值，避免微小滚动触发
    }
  },
  created() {
    this.fetchPlanList()
    this.fetchStatistics()
  },
  
  mounted() {
    this.setupDatePickerStyles()
  },
  
  beforeDestroy() {
    if (this._pickerObserver) {
      this._pickerObserver.disconnect()
    }
  },
  computed: {
    renderedPlanInfo() {
      const src = (this.currentPlan && this.currentPlan.plan_info) ? this.currentPlan.plan_info : ''

      // 暂时禁用预处理，直接使用原始内容
      // const processedSrc = this.preprocessTableFormat(src)

      // 自定义链接渲染：新窗口打开
      const renderer = new marked.Renderer()
      renderer.link = function(href, title, text) {
        const t = title ? ` title="${title}"` : ''
        return `<a href="${href}" target="_blank" rel="noopener noreferrer"${t}>${text}</a>`
      }

      // 自定义表格渲染：确保表格结构完整
      renderer.table = function(header, body) {
        return `<table class="markdown-table">\n<thead>\n${header}</thead>\n<tbody>\n${body}</tbody>\n</table>`
      }

      // 任务列表渲染：为包含复选框的列表增加类名
      renderer.list = function(body, ordered, start) {
        const hasTaskItems = body.includes('<input type="checkbox"')
        const listType = ordered ? 'ol' : 'ul'
        const startAttr = ordered && start !== 1 ? ` start="${start}"` : ''
        if (hasTaskItems) {
          return `<${listType} class="task-list"${startAttr}>\n${body}</${listType}>\n`
        }
        return `<${listType}${startAttr}>\n${body}</${listType}>\n`
      }
      renderer.listitem = function(text) {
        if (text.includes('<input type="checkbox"')) {
          return `<li class="task-list-item">${text}</li>\n`
        }
        return `<li>${text}</li>\n`
      }

      // 预处理 markdown，将 - [ ] / - [x] 转为禁用复选框
      const originalLex = marked.Lexer.lex
      marked.Lexer.lex = function(src, options) {
        // 同时处理 "- [ ]" "* [ ]" "+ [ ]" 三种无序前缀
        src = src.replace(/^(?:-|\*|\+) \[([ x])\] (.+)$/gm, function(_m, checked, text) {
          const attr = checked === 'x' ? 'checked' : ''
          return `- <input type=\"checkbox\" ${attr} disabled> ${text}`
        })
        return originalLex.call(this, src, options)
      }

      const html = marked(src, { gfm: true, breaks: true, renderer })

      // 渲染 KaTeX 数学公式
      const withBlockMath = html.replace(/\$\$([\s\S]+?)\$\$/g, (_, expr) => {
        try { return katex.renderToString(expr, { displayMode: true, throwOnError: false }) } catch(e) { return _ }
      })
      const withInlineMath = withBlockMath.replace(/(^|[^$])\$([^$]+?)\$([^$]|$)/g, (m, pre, expr, post) => {
        try { return pre + katex.renderToString(expr, { displayMode: false, throwOnError: false }) + post } catch(e) { return m }
      })

      // 配置DOMPurify允许表格相关的标签和属性
      const cleanHtml = DOMPurify.sanitize(withInlineMath, {
        ALLOWED_TAGS: [
          'p', 'br', 'strong', 'em', 'u', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
          'ul', 'ol', 'li', 'blockquote', 'code', 'pre', 'a', 'img', 'input',
          'table', 'thead', 'tbody', 'tr', 'th', 'td', 'caption'
        ],
        ALLOWED_ATTR: [
          'href', 'target', 'rel', 'title', 'class', 'id', 'src', 'alt',
          'type', 'disabled', 'checked'
        ],
        KEEP_CONTENT: true
      })

      return cleanHtml
    }
  },
  
  methods: {
    // 预处理表格格式：将非标准的表格格式转换为标准markdown表格
    preprocessTableFormat(content) {
      if (!content) return content
      
      const lines = content.split('\n')
      const processedLines = []
      let inTable = false
      let tableLines = []
      
      for (let i = 0; i < lines.length; i++) {
        const line = lines[i].trim()
        
        // 检查是否是分隔行（只包含 --- 和空格）
        const isSeparatorLine = /^[\s\-|]+$/.test(line) && line.includes('---')
        
        // 检查是否是表格行（包含 | 分隔符或者看起来像表格的格式）
        const hasPipeSeparators = line.includes('|') && line.split('|').length >= 3
        const looksLikeTableRow = !hasPipeSeparators && !isSeparatorLine && 
                                 (line.includes('序号') || /^\d+\s+[\u4e00-\u9fa5]+\s+[\d\.\-\s~]+/.test(line))
        
        if (hasPipeSeparators || looksLikeTableRow) {
          if (!inTable) {
            inTable = true
            tableLines = []
          }
          tableLines.push(line)
        } else if (isSeparatorLine) {
          // 如果是分隔行，且之前有表格行，则添加到表格中
          if (inTable && tableLines.length > 0) {
            tableLines.push(line)
          } else {
            // 如果之前没有表格行，则作为普通文本处理
            processedLines.push(lines[i])
          }
        } else {
          // 如果之前有表格，先处理表格
          if (inTable && tableLines.length > 0) {
            processedLines.push(...this.convertToMarkdownTable(tableLines))
            tableLines = []
            inTable = false
          }
          processedLines.push(lines[i])
        }
      }
      
      // 处理最后的表格
      if (inTable && tableLines.length > 0) {
        processedLines.push(...this.convertToMarkdownTable(tableLines))
      }
      
      return processedLines.join('\n')
    },
    
    // 将表格行转换为标准markdown表格格式
    convertToMarkdownTable(tableLines) {
      if (tableLines.length === 0) return []
      
      const result = []
      let hasSeparator = false
      
      // 检查是否已经有分隔行
      for (const line of tableLines) {
        if (/^[\s\-|]+$/.test(line) && line.includes('---')) {
          hasSeparator = true
          break
        }
      }
      
      // 处理每一行
      for (let i = 0; i < tableLines.length; i++) {
        const line = tableLines[i]
        
        // 如果是分隔行，跳过（避免重复）
        if (/^[\s\-|]+$/.test(line) && line.includes('---')) {
          continue
        }
        
        // 将行转换为标准markdown表格格式
        const convertedLine = this.convertLineToMarkdownTable(line)
        result.push(convertedLine)
        
        // 如果是第一行（表头），且没有分隔行，则添加分隔行
        if (i === 0 && !hasSeparator) {
          const columns = convertedLine.split('|').length - 2 // 减去开头和结尾的空字符串
          const separator = '|' + '---|'.repeat(columns - 1)
          result.push(separator)
        }
      }
      
      return result
    },
    
    // 将单行转换为markdown表格格式
    convertLineToMarkdownTable(line) {
      // 如果已经有 | 分隔符，直接返回
      if (line.includes('|')) {
        return line
      }
      
      // 将空格分隔的内容转换为 | 分隔的格式
      const parts = line.split(/\s+/).filter(part => part.trim() !== '')
      return '|' + parts.join('|') + '|'
    },
    
    // 设置日期选择器样式
    setupDatePickerStyles() {
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.addedNodes.length > 0) {
            const pickers = document.querySelectorAll('.el-picker-panel')
            pickers.forEach(picker => {
              picker.style.left = '50%'
              picker.style.transform = 'translateX(-50%)'
              picker.style.margin = '0'
              picker.style.zIndex = '2500'
              
              if (picker.style.top === '50%') {
                picker.style.top = ''
              }
              
              const arrow = picker.querySelector('.popper__arrow')
              if (arrow) {
                arrow.style.display = 'none'
              }
            })
          }
        })
      })
      
      observer.observe(document.body, { childList: true, subtree: true })
      this._pickerObserver = observer
    },
    
    // 获取计划列表
    async fetchPlanList(isLoadMore = false) {
      try {
        this.loading = true
        const params = {
          pageNum: this.currentPage,
          pageSize: this.pageSize,
          sort_prop: this.sortProp,
          sort_order: this.sortOrder
        }
        
        // 添加查询参数
        if (this.filterForm.plan_name) {
          params.plan_name = this.filterForm.plan_name
        }
        
        if (this.filterForm.plan_info) {
          params.plan_info = this.filterForm.plan_info
        }
        
        if (this.filterForm.plan_status !== '') {
          params.plan_status = this.filterForm.plan_status
        }
        
        if (this.filterForm.add_time && this.filterForm.add_time.length === 2) {
          params.add_time_start = this.filterForm.add_time[0]
          params.add_time_end = this.filterForm.add_time[1]
        }
        
        const res = await getPlanList(params)
        if (res.code === 200) {
          const items = Array.isArray(res.data.items) ? res.data.items : []
          if (isLoadMore) {
            this.planList = [...this.planList, ...items]
          } else {
            this.planList = items
          }
          this.total = Number(res.data.total || 0)
          const reachedEnd = (this.currentPage * this.pageSize) >= this.total || items.length === 0
          this.hasMore = !reachedEnd
        } else {
          this.$message.error(res.msg || '获取计划列表失败')
          this.hasMore = false
        }
      } catch (error) {
        console.error('获取计划列表失败:', error)
        this.$message.error('获取计划列表失败')
      } finally {
        this.loading = false
      }
    },
    
    // 获取统计数据
    async fetchStatistics() {
      try {
        const res = await getPlanStats()
        if (res.code === 200) {
          const data = res.data || {}
          const toNumber = (val) => {
            const n = Number(val)
            return Number.isFinite(n) ? n : 0
          }

          const total = toNumber(data.total)
          const notStarted = toNumber(data.not_started)
          const inProgress = toNumber(data.in_progress)
          const completed = toNumber(data.completed)
          const terminated = toNumber(data.terminated)

          // 兼容不同后端字段命名；若未返回暂停字段，则通过总数减各状态推导
          const apiPaused = [data.paused, data.pause, data.paused_count, data.on_hold]
            .map(item => Number(item))
            .find(item => Number.isFinite(item))
          const derivedPaused = Math.max(total - notStarted - inProgress - completed - terminated, 0)
          const paused = Number.isFinite(apiPaused) ? apiPaused : derivedPaused

          this.stats = {
            ...this.stats,
            ...data,
            total,
            not_started: notStarted,
            in_progress: inProgress,
            completed,
            terminated,
            paused
          }

          // 计算完成率和进行率
          if (this.stats.total > 0) {
            this.stats.completion_rate = (this.stats.completed / this.stats.total) * 100
            this.stats.progress_rate = (this.stats.in_progress / this.stats.total) * 100
          } else {
            this.stats.completion_rate = 0
            this.stats.progress_rate = 0
          }
        }
      } catch (error) {
        console.error('获取统计数据失败:', error)
      }
    },
    
    // 显示统计信息弹窗
    showStatisticsDialog() {
      this.statisticsDialogVisible = !this.statisticsDialogVisible
    },
    
    // 显示查询弹窗
    showFilterDialog() {
      this.filterDialogVisible = !this.filterDialogVisible
    },
    
    // 处理卡片点击
    handleCardClick(plan) {
      if (plan && plan._showSwipeActions) {
        this.$set(plan, '_showSwipeActions', false)
        return
      }
      this.currentPlan = plan
      this.detailDialogVisible = true
    },
    
    // 处理查询
    handleSearch() {
      this.currentPage = 1
      this.planList = []
      this.fetchPlanList()
      this.fetchStatistics()
      this.filterDialogVisible = false
    },
    
    // 重置查询条件
    handleReset() {
      this.filterForm = {
        plan_name: '',
        plan_info: '',
        plan_status: '',
        add_time: []
      }
    },
    
    // 加载更多
    loadMore() {
      if (!this.hasMore || this.loading) return
      
      this.currentPage++
      this.fetchPlanList(true)
    },
    
    // 处理滚动事件
    handleScroll(event) {
      const container = event.target
      const scrollHeight = container.scrollHeight
      const scrollTop = container.scrollTop
      const clientHeight = container.clientHeight
      
      // 检测滚动方向并控制头部横幅显示
      const deltaY = scrollTop - this.lastScrollTop
      
      if (Math.abs(deltaY) > this.scrollThreshold) {
        if (deltaY > 0) {
          // 向下滚动（内容向上移动），隐藏头部横幅
          this.headerVisible = false
        } else {
          // 向上滚动（内容向下移动），显示头部横幅
          this.headerVisible = true
        }
      }
      
      this.lastScrollTop = scrollTop
      
      // 原有的加载更多逻辑（仅在满足条件时）
      if (!this.loading && this.hasMore && scrollHeight - scrollTop - clientHeight < 100) {
        this.loadMore()
      }
      this.hideAllSwipeActions()
    },
    
    // 格式化日期
    formatDate(date) {
      if (!date) return '未设置'
      // 处理默认值或无效日期
      if (date === '0000-00-00 00:00:00' || date === '0000-00-00' || date.startsWith('0000-00-00')) {
        return '未设置'
      }
      return date.split(' ')[0]
    },
    
    // 格式化数字
    formatNumber(value) {
      if (value === undefined || value === null) return '0'
      return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")
    },
    
    // 格式化百分比
    formatPercentage(value) {
      if (value === undefined || value === null) return '0.00%'
      return value.toFixed(2) + '%'
    },

    // 解析日期字符串（兼容 ISO 与 MySQL 格式）
    parseDateValue(date) {
      if (!date || date === '0000-00-00 00:00:00' || date === '0000-00-00' || date.startsWith('0000-00-00')) {
        return null
      }
      const parsed = new Date(date.replace(' ', 'T'))
      return Number.isNaN(parsed.getTime()) ? null : parsed
    },

    // 计算已完成计划的耗时（向上取整天数）
    getDurationDays(plan) {
      if (!plan || plan.plan_status !== 2) return null
      const start = this.parseDateValue(plan.start_time)
      const end = this.parseDateValue(plan.end_time)
      if (!start || !end || end < start) return null
      const diffMs = end.getTime() - start.getTime()
      return Math.ceil(diffMs / (24 * 60 * 60 * 1000))
    },

    // 格式化耗时展示文本
    formatDurationDays(plan) {
      const durationDays = this.getDurationDays(plan)
      if (durationDays === null) return '--'
      return `${durationDays}天`
    },
    
    // 获取状态类型
    getStatusType(status) {
      const typeMap = {
        0: 'info',    // 未开始
        1: 'primary', // 进行中
        2: 'success', // 已完成
        3: 'warning', // 已暂停
        4: 'danger'   // 已终止
      }
      return typeMap[status] || ''
    },
    
         // 获取状态文本
     getStatusText(status) {
       const textMap = {
         0: '未开始',
         1: '进行中',
         2: '已完成',
         3: '已暂停',
         4: '已终止'
       }
       return textMap[status] || '未知'
     },
     
     // 获取状态印章样式类名
     getStatusStampClass(status) {
       const classMap = {
         0: 'status-not-started',    // 未开始 - 灰色
         1: 'status-in-progress',    // 进行中 - 蓝色
         2: 'status-completed',      // 已完成 - 绿色
         3: 'status-paused',         // 已暂停 - 橙色
         4: 'status-terminated'      // 已终止 - 红色
       }
       return classMap[status] || 'status-not-started'
     },
     
     // 获取状态印章文本
     getStatusStampText(status) {
       const textMap = {
         0: '未开始',    // 未开始
         1: '进行中',    // 进行中
         2: '已完成',    // 已完成
         3: '已暂停',    // 已暂停
         4: '已终止'     // 已终止
       }
       return textMap[status] || '未开始'
     },

     // 显示/隐藏所有卡片的滑动操作
     hideAllSwipeActions() {
       this.planList.forEach(p => {
         if (p._showSwipeActions) this.$set(p, '_showSwipeActions', false)
       })
     },

     onTouchStart(e, plan) {
       const touch = e.changedTouches[0]
       this.touchStartX = touch.clientX
       this.touchStartY = touch.clientY
       this.isTouchMoving = false
       this.ignoreEdgeSwipe = this.touchStartX <= 25
     },

     onTouchMove(e, plan) {
       const touch = e.changedTouches[0]
       const deltaX = touch.clientX - this.touchStartX
       const deltaY = touch.clientY - this.touchStartY
       if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 20) {
         this.isTouchMoving = true
       }
     },

     onTouchEnd(e, plan) {
       if (!this.isTouchMoving) return
       const touch = e.changedTouches[0]
       const deltaX = touch.clientX - this.touchStartX
       // 从左边缘起势的右滑交由系统返回，不处理
       if (this.ignoreEdgeSwipe && deltaX > 0) {
         this.isTouchMoving = false
         return
       }
       // 左滑展开，右滑收起
       if (deltaX < -40) {
         this.hideAllSwipeActions()
         this.$set(plan, '_showSwipeActions', true)
       } else if (deltaX > 40) {
         this.$set(plan, '_showSwipeActions', false)
       }
       this.isTouchMoving = false
     },

     // 状态按钮可用性
     canStart(plan) {
       if (!plan) return false
       return plan.plan_status === 0 || plan.plan_status === 3
     },
     canPause(plan) {
       if (!plan) return false
       return plan.plan_status === 1
     },
     canComplete(plan) {
       if (!plan) return false
       return plan.plan_status === 1
     },
     canTerminate(plan) {
       if (!plan) return false
       return plan.plan_status === 1 || plan.plan_status === 3
     },

     // 与PC端一致的交互
     async handleStart(plan) {
       if (!this.canStart(plan) || this.actionLoading) return
       try {
         await this.mobileConfirm('确认开始该计划吗？')
         this.actionLoading = true
         await updatePlanStatus(plan.plan_code, 1)
         this.applyLocalStatus(plan, 1)
         await this.refreshAfterAction(plan)
       } catch (error) {
         if (error !== 'cancel') this.$message.error('操作失败')
       } finally {
         this.actionLoading = false
       }
     },
     async handlePause(plan) {
       if (!this.canPause(plan) || this.actionLoading) return
       try {
         await this.mobileConfirm('确认暂停该计划吗？')
         this.actionLoading = true
         await updatePlanStatus(plan.plan_code, 3)
         this.applyLocalStatus(plan, 3)
         await this.refreshAfterAction(plan)
       } catch (error) {
         if (error !== 'cancel') this.$message.error('操作失败')
       } finally {
         this.actionLoading = false
       }
     },
     async handleComplete(plan) {
       if (!this.canComplete(plan) || this.actionLoading) return
       try {
         await this.mobileConfirm('确认完成该计划吗？')
         this.actionLoading = true
         await updatePlanStatus(plan.plan_code, 2)
         this.applyLocalStatus(plan, 2)
         await this.refreshAfterAction(plan)
       } catch (error) {
         if (error !== 'cancel') this.$message.error('操作失败')
       } finally {
         this.actionLoading = false
       }
     },
     async handleTerminate(plan) {
       if (!this.canTerminate(plan) || this.actionLoading) return
       try {
         await this.mobileConfirm('确认终止该计划吗？')
         this.actionLoading = true
         await updatePlanStatus(plan.plan_code, 4)
         this.applyLocalStatus(plan, 4)
         await this.refreshAfterAction(plan)
       } catch (error) {
         if (error !== 'cancel') this.$message.error('操作失败')
       } finally {
         this.actionLoading = false
       }
     },

     // 简洁移动端确认（替代默认消息框样式）
     mobileConfirm(message) {
       return new Promise((resolve, reject) => {
         this.$confirm(message, '提示', {
           confirmButtonText: '确定',
           cancelButtonText: '取消',
           type: 'warning',
           customClass: 'mobile-confirm-dialog',
           center: true,
           closeOnClickModal: true,
         }).then(() => resolve()).catch((err) => reject(err || 'cancel'))
       })
     },

     // 刷新：立即更新当前卡片的状态，随后刷新列表与统计
     async refreshAfterAction(plan) {
       this.$message.success('操作成功')
       this.hideAllSwipeActions()
       // 重置分页，从第一页重新拉取，避免在高页码刷新出现连续请求
       this.currentPage = 1
       this.hasMore = true
       await this.fetchPlanList(false)
       await this.fetchStatistics()
       // 同步更新当前弹窗内的数据
       if (this.currentPlan) {
         const updated = this.planList.find(p => p.plan_code === this.currentPlan.plan_code)
         if (updated) this.currentPlan = { ...updated }
       }
     },

     // 本地同步状态，立刻更新列表项与弹窗内的对象
     applyLocalStatus(plan, status) {
       if (!plan) return
       plan.plan_status = status
       if (this.currentPlan && this.currentPlan.plan_code === plan.plan_code) {
         this.currentPlan = { ...this.currentPlan, plan_status: status }
       }
       const idx = this.planList.findIndex(p => p.plan_code === plan.plan_code)
       if (idx !== -1) {
         this.$set(this.planList, idx, { ...this.planList[idx], plan_status: status })
       }
     }
  }
}
</script>

<style lang="scss" scoped>
.plan-mobile-container {
  height: 100vh;
  display: flex;
  flex-direction: column;
  background-color: #f0f2f5;
  position: relative;
  overflow: hidden;
}

/* 移动端操作按钮 */
.mobile-action-buttons {
  position: fixed;
  right: 15px;
  bottom: 135px;
  display: flex;
  flex-direction: column;
  gap: 15px;
  z-index: 2001;
  width: 40px;
  align-items: center;
  padding: 0 !important;
  margin: 0 !important;
  
  .el-button {
    margin: 0 !important;
    padding: 0 !important;
    box-sizing: border-box !important;
  }
}

.mobile-icon-btn {
  width: 40px !important;
  height: 40px !important;
  border-radius: 40px !important;
  background-color: rgba(255, 255, 255, 0.5) !important;
  color: #409eff !important;
  border: none !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1) !important;
  display: flex !important;
  justify-content: center !important;
  align-items: center !important;
  padding: 0 !important;
  margin: 0 !important;
  transition: all 0.3s ease !important;
  position: relative !important;
  left: 0 !important;
  
  i {
    font-size: 20px !important;
    margin: 0 !important;
    padding: 0 !important;
    display: inline-flex !important;
    justify-content: center !important;
    align-items: center !important;
    width: 20px !important;
    height: 20px !important;
  }
  
  &:hover, &:focus {
    background-color: rgba(255, 255, 255, 0.8) !important;
    transform: scale(1.05) !important;
  }
  
  &:active {
    background-color: rgba(255, 255, 255, 0.9) !important;
    transform: scale(0.95) !important;
  }
}

/* 标题栏 */
.mobile-header {
  height: 50px;
  background-color: #304156;
  background-image: linear-gradient(to bottom, rgba(255, 255, 255, 0.05), transparent);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 10;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  transition: all 0.3s ease;
  
  .page-title {
    font-size: 18px;
    font-weight: 600;
    color: #ffffff;
    margin: 0;
  }
  
  /* 隐藏状态 */
  &.header-hidden {
    transform: translateY(-100%);
    opacity: 0;
  }
}

/* 卡片容器 */
.plan-cards-container {
  flex: 1;
  overflow-y: auto;
  padding: 10px;
  padding-bottom: 20px;
  padding-top: 60px; /* 为固定定位的头部预留空间 */
  box-sizing: border-box;
  scrollbar-width: none;
  -ms-overflow-style: none;
  touch-action: pan-y; /* 允许纵向滚动，减少横向手势干扰 */
  transition: padding-top 0.3s ease; /* 添加顶部内边距的过渡动画 */
  
  &::-webkit-scrollbar {
    display: none;
  }
  
  /* 当头部隐藏时，减少顶部内边距 */
  &.header-hidden {
    padding-top: 10px;
  }
  
  /* 计划卡片 */
  .plan-card {
    background: #fff;
    border-radius: 10px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
    margin-bottom: 14px;
    overflow: hidden;
    transition: all 0.3s ease;
    width: 100%;
    display: flex;
    align-items: flex-start;
    position: relative;
    border: 1px solid #ebeef5;
    
    &:active {
      transform: scale(0.98);
      box-shadow: 0 2px 6px rgba(0, 0, 0, 0.06);
    }
    
    &:last-child {
      margin-bottom: 20px;
    }
    
    /* 状态印章 */
    .status-stamp {
      position: absolute;
      top: 45px;
      right: 10%;
      transform: translateY(-50%) rotate(-15deg);
      pointer-events: none;
      z-index: 2;
      
      .stamp-content {
        width: 60px;
        height: 32px;
        border-radius: 16px;
        color: white;
        display: flex;
        justify-content: center;
        align-items: center;
        font-size: 14px;
        font-weight: bold;
        border: 2px solid rgba(255, 255, 255, 0.6);
        box-shadow: 0 0 8px rgba(0, 0, 0, 0.15);
        text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
        
        /* 未开始 - 灰色 */
        &.status-not-started {
          background-color: rgba(144, 147, 153, 0.6);
        }
        
        /* 进行中 - 蓝色 */
        &.status-in-progress {
          background-color: rgba(64, 158, 255, 0.6);
        }
        
        /* 已完成 - 绿色 */
        &.status-completed {
          background-color: rgba(103, 194, 58, 0.6);
        }
        
        /* 已暂停 - 橙色 */
        &.status-paused {
          background-color: rgba(230, 162, 60, 0.6);
        }
        
        /* 已终止 - 红色 */
        &.status-terminated {
          background-color: rgba(245, 108, 108, 0.6);
        }
      }
    }

    /* 滑动显示的操作按钮覆盖层 */
    .swipe-actions {
      position: absolute;
      bottom: 0;
      right: -200px; /* 增加宽度，确保所有按钮都能完全显示 */
      width: 200px; /* 增加容器宽度，确保背景能覆盖所有按钮 */
      height: 60px; /* 固定高度 */
      background: linear-gradient(135deg, 
        rgba(255, 255, 255, 0.95) 0%, 
        rgba(248, 249, 250, 0.95) 50%, 
        rgba(240, 242, 245, 0.95) 100%);
      backdrop-filter: blur(8px);
      border: 1px solid rgba(235, 238, 245, 0.8);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08), 
                  0 2px 4px rgba(0, 0, 0, 0.04);
      display: flex;
      align-items: center;
      justify-content: center;
      transition: right 0.3s ease;
      z-index: 3;
      border-radius: 12px 0 0 0;
      
      &.show {
        right: 0;
      }
      
      .action-buttons {
        display: flex;
        gap: 12px; /* 增加按钮之间的间距 */
        padding-left: 15px; /* 添加左侧内边距，确保第一个按钮有足够的左间距 */
        padding-right: 15px; /* 添加右侧内边距，确保最后一个按钮有足够的右间距 */
        
        .action-icon-btn {
          width: 36px !important;
          height: 36px !important;
          min-width: 36px !important;
          padding: 0 !important;
          border-radius: 8px !important;
          background-color: #F2F3F5 !important;
          border: none !important;
          display: inline-flex !important;
          align-items: center !important;
          justify-content: center !important;
          margin: 0 !important;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.06);
          transition: all 0.2s ease;
        }
        .action-icon-btn i { font-size: 18px !important; }
        .action-icon-btn:hover { 
          background-color: #E5E6EB !important; 
          transform: translateY(-1px);
          box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
        }
        .action-icon-btn:active {
          transform: translateY(0);
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.06);
        }
        .action-icon-btn:disabled { background-color: #F2F3F5 !important; opacity: 1 !important; }
        .action-icon-btn:disabled i { color: #C0C4CC !important; }
        .action-icon-btn i.el-icon-video-play { color: #67C23A; }
        .action-icon-btn i.el-icon-video-pause { color: #E6A23C; }
        .action-icon-btn i.el-icon-check { color: #67C23A; }
        .action-icon-btn i.el-icon-circle-close { color: #F56C6C; }
      }
    }

    /* icon-only action button (both overlay and detail) */
    .action-icon-btn {
      width: 36px !important;
      height: 36px !important;
      min-width: 36px !important;
      padding: 0 !important;
      border-radius: 8px !important;
      background-color: #F2F3F5 !important;
      border: none !important;
      display: inline-flex !important;
      align-items: center !important;
      justify-content: center !important;
      margin: 0 !important;
    }
    .action-icon-btn i {
      font-size: 18px !important;
    }
    .action-icon-btn:hover {
      background-color: #E5E6EB !important;
    }
    .action-icon-btn:disabled {
      background-color: #F2F3F5 !important;
      opacity: 1 !important;
    }
    .action-icon-btn:disabled i {
      color: #C0C4CC !important;
    }

    /* per-PC color mapping */
    .action-icon-btn i.el-icon-video-play { color: #67C23A; }
    .action-icon-btn i.el-icon-video-pause { color: #E6A23C; }
    .action-icon-btn i.el-icon-check { color: #67C23A; }
    .action-icon-btn i.el-icon-circle-close { color: #F56C6C; }

    .plan-card-main {
      padding: 15px;
      padding-top: 10px;
      flex: 1;
      width: 100%;
      
      .plan-header {
        display: flex;
        align-items: center;
        margin-bottom: 10px;
        padding-right: 0px;
        /* 移除 min-height，让高度根据内容自适应 */
        
        .plan-name {
          font-size: 15px;
          font-weight: 600;
          color: #303133;
          line-height: 1.4;
          margin: 0;
          padding: 0;
          display: flex;
          align-items: center;
          gap: 8px;

          .duration-tag {
            flex-shrink: 0;
            padding: 2px 8px;
            border-radius: 12px;
            font-size: 11px;
            font-weight: 600;
            color: #409EFF;
            background: rgba(64, 158, 255, 0.12);
            border: 1px solid rgba(64, 158, 255, 0.35);
            line-height: 1.4;
            white-space: nowrap;
          }

          .plan-name-text {
            flex: 1;
            min-width: 0;
            word-break: break-word;
            display: -webkit-box;
            line-clamp: 2;
            -webkit-line-clamp: 2;
            -webkit-box-orient: vertical;
            overflow: hidden;
            text-overflow: ellipsis;
          }
        }
      }
      
      .plan-info {
        margin-top: 3px;
        border-top: 1px solid #f5f5f5;
        padding-top: 8px;
        
        .plan-source-time {
          display: flex;
          align-items: center;
          margin-bottom: 8px;
          background-color: rgba(249, 250, 252, 0.8);
          border-radius: 6px;
          padding: 6px 10px;
          border: 1px solid #f0f2f5;
          
          .source-label {
            font-size: 12px;
            color: #606266;
            margin-right: 5px;
            min-width: 40px;
            font-weight: 500;
          }
          
          .source-value {
            font-size: 12px;
            color: #303133;
            font-weight: 500;
            
            &.manual-add {
              color: #409EFF;
              font-weight: 600;
            }
            
            &.review-generate {
              color: #E6A23C;
              font-weight: 600;
            }
          }
        }
        
        .plan-question {
          display: flex;
          align-items: center;
          margin-bottom: 8px;
          background-color: rgba(249, 250, 252, 0.8);
          border-radius: 6px;
          padding: 6px 10px;
          border: 1px solid #f0f2f5;
          
          .question-label {
            font-size: 12px;
            color: #606266;
            margin-right: 5px;
            min-width: 40px;
            font-weight: 500;
          }
          
          .question-value {
            font-size: 12px;
            color: #303133;
            font-weight: 500;
            flex: 1;
            word-break: break-word;
            
            &.question-link {
              color: #409EFF;
              text-decoration: none;
              
              &:hover {
                text-decoration: underline;
              }
            }
          }
        }
        
        .plan-time-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          grid-gap: 6px;
          
          // 如果前面有另一个 plan-time-grid，增加上边距
          &:not(:first-of-type) {
            margin-top: 10px;
          }
          
          .time-item {
            font-size: 12px;
            display: flex;
            align-items: center;
            background-color: rgba(249, 250, 252, 0.8);
            border-radius: 4px;
            padding: 4px 8px;
            border: 1px solid #f0f2f5;
            
            .time-label {
              color: #606266;
              margin-right: 3px;
              font-weight: 500;
              min-width: 35px;
              text-align: right;
            }
            
            .time-value {
              color: #303133;
              font-weight: 500;
            }
          }
        }
      }
    }
  }
  
  /* 暂无数据样式 */
  .no-data {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 300px;
    
    .no-data-content {
      text-align: center;
      padding: 30px;
      
      i {
        font-size: 50px;
        color: #dcdfe6;
        margin-bottom: 20px;
      }
      
      .no-data-title {
        font-size: 18px;
        color: #606266;
        font-weight: 500;
        margin-bottom: 10px;
        display: block;
      }
      
      .no-data-tips {
        font-size: 14px;
        color: #909399;
      }
    }
  }
  
  /* 加载更多区域 */
  .load-more, .loading-more, .no-more {
    text-align: center;
    margin-top: 10px;
    padding: 10px 0;
    
    .el-button {
      font-size: 14px;
    }
  }
  
  .loading-more {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 5px;
    color: #909399;
    
    i {
      font-size: 16px;
    }
  }
  
  .no-more {
    text-align: center;
    padding-bottom: 20px;
    
    span {
      color: #909399;
      font-size: 12px;
    }
    
    .copyright-info {
      text-align: center;
      width: 100%;
      margin: 15px auto 0;
      display: block;
      padding: 0 15px 15px;
      box-sizing: border-box;
      
      .mobile-copyright {
        text-align: center;
        width: 100%;
        color: #909399;
        font-size: 12px;
        line-height: 1.5;
      }
    }
  }
}

/* 移动端统计网格 */
.mobile-statistics-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 8px;
  padding: 2px 0;
  max-height: none;
  overflow-y: visible;
  
  .stat-item {
    background-color: #f9fafc;
    border-radius: 8px;
    padding: 8px 5px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
    transition: all 0.3s ease;
    position: relative;
    overflow: hidden;
    min-height: 65px;
    height: auto;
    
    &:active {
      transform: scale(0.98);
    }
    
    /* 给不同卡片添加渐变背景色 */
    &:nth-child(1) {
      background: linear-gradient(to top, 
        rgba(226, 206, 186, 0.3) 0%, 
        rgba(226, 206, 186, 1) 100%);
      .stat-label, i { color: #8D7B64; }
      .stat-value { color: #6D5B44; }
    }
    &:nth-child(2) {
      background: linear-gradient(to top, 
        rgba(209, 178, 164, 0.3) 0%, 
        rgba(209, 178, 164, 1) 100%);
      .stat-label, i { color: #956B5A; }
      .stat-value { color: #754B3A; }
    }
    &:nth-child(3) {
      background: linear-gradient(to top, 
        rgba(190, 206, 185, 0.3) 0%, 
        rgba(190, 206, 185, 1) 100%);
      .stat-label, i { color: #5B7155; }
      .stat-value { color: #3B5135; }
    }
    &:nth-child(4) {
      background: linear-gradient(to top, 
        rgba(199, 188, 205, 0.3) 0%, 
        rgba(199, 188, 205, 1) 100%);
      .stat-label, i { color: #6B5873; }
    }
    &:nth-child(5) {
      background: linear-gradient(to top, 
        rgba(187, 199, 214, 0.3) 0%, 
        rgba(187, 199, 214, 1) 100%);
      .stat-label, i { color: #4A5F78; }
      .stat-value { color: #2A3F58; }
    }
    &:nth-child(6) {
      background: linear-gradient(to top, 
        rgba(220, 198, 170, 0.3) 0%, 
        rgba(220, 198, 170, 1) 100%);
      .stat-label, i { color: #8B7447; }
      .stat-value { color: #6B5427; }
    }
    &:nth-child(7) {
      background: linear-gradient(to top, 
        rgba(215, 185, 185, 0.3) 0%, 
        rgba(215, 185, 185, 1) 100%);
      .stat-label, i { color: #8E5D5D; }
      .stat-value { color: #6E3D3D; }
    }
    &:nth-child(8) {
      background: linear-gradient(to top, 
        rgba(176, 200, 197, 0.3) 0%, 
        rgba(176, 200, 197, 1) 100%);
      .stat-label, i { color: #4A6764; }
      .stat-value { color: #2A4744; }
    }

    &:nth-child(8) {
      background: linear-gradient(to top, 
        rgba(180, 195, 203, 0.3) 0%, 
        rgba(180, 195, 203, 1) 100%);
      .stat-label, i { color: #546E7A; }
      .stat-value { color: #37474F; }
    }
    
    .stat-value {
      font-size: 16px;
      font-weight: 600;
      margin-bottom: 5px;
      text-align: center;
      line-height: 1.2;
    }
    
    .stat-label {
      font-size: 11px;
      display: flex;
      align-items: center;
      justify-content: center;
      
      i {
        margin-right: 3px;
        font-size: 13px;
      }
    }
  }
}

/* 调整弹窗样式以适应统计卡片 */
.statistics-dialog-body {
  max-height: none;
  overflow-y: visible;
  padding: 12px 10px;
}

/* 自定义统计对话框标题 */
.dialog-custom-title {
  font-size: 16px;
  text-align: center;
  margin-bottom: 15px;
  padding-bottom: 10px;
  border-bottom: 1px solid #ebeef5;
}

/* 弹窗中的详情样式 */
.plan-detail {
  height: 100%;
  display: flex;
  flex-direction: column;
  position: relative; /* 作为绝对定位按钮的定位上下文 */
  
  .detail-scrollable-content {
    flex: 1 1 auto;
    min-height: 0; /* 允许该区域在容器内收缩，从而产生滚动 */
    overflow-y: auto;
    -webkit-overflow-scrolling: touch; /* 移动端流畅滚动 */
    /* 限制可视高度，确保上半部分在内容超出时滚动，而不挤压按钮区域 */
    max-height: calc(80vh - 120px);
    padding-bottom: 72px; /* 预留给底部按钮区域的空间，避免被遮挡 */
    padding: 20px 20px 20px 20px; /* 添加内边距 */
    scrollbar-width: none;
    -ms-overflow-style: none;
    
    &::-webkit-scrollbar {
      display: none;
    }
  }
  
  .detail-actions-fixed {
    background-color: #ffffff;
    border-top: 1px solid #ebeef5;
    padding: 12px 20px; /* 固定的内边距 */
    flex: 0 0 auto;
    margin-top: auto; /* 内容少时推到最底部 */
    position: relative;
    z-index: 1;
  }
  
  .detail-header {
    border-bottom: 1px solid #ebeef5;
    padding-bottom: 15px;
    margin-bottom: 15px;
    position: relative;
    
    h3 {
      font-size: 18px;
      font-weight: 600;
      color: #303133;
      margin: 0;
      word-break: break-word;
    }
    
    .detail-status {
      position: absolute;
      top: 30px;
      right: 45px;
      transform: rotate(-15deg);
      
      .stamp-content {
        width: 60px;
        height: 32px;
        border-radius: 16px;
        color: white;
        display: flex;
        justify-content: center;
        align-items: center;
        font-size: 14px;
        font-weight: bold;
        border: 2px solid rgba(255, 255, 255, 0.6);
        box-shadow: 0 0 8px rgba(0, 0, 0, 0.15);
        text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
        
        /* 未开始 - 灰色 */
        &.status-not-started {
          background-color: rgba(144, 147, 153, 0.6);
        }
        
        /* 进行中 - 蓝色 */
        &.status-in-progress {
          background-color: rgba(64, 158, 255, 0.6);
        }
        
        /* 已完成 - 绿色 */
        &.status-completed {
          background-color: rgba(103, 194, 58, 0.6);
        }
        
        /* 已暂停 - 橙色 */
        &.status-paused {
          background-color: rgba(230, 162, 60, 0.6);
        }
        
        /* 已终止 - 红色 */
        &.status-terminated {
          background-color: rgba(245, 108, 108, 0.6);
        }
      }
    }
  }
  
  .detail-content {
    .detail-item {
      display: flex;
      margin-bottom: 15px;
      
      .detail-label {
        min-width: 80px;
        color: #909399;
        font-size: 14px;
        margin-right: 12px;
      }
      
      .detail-value {
        flex: 1;
        font-size: 14px;
        color: #606266;
        font-weight: 500;
        word-break: break-word;

        &.time-range {
          display: inline-flex;
          align-items: center;
          gap: 6px;
        }

        .time-separator {
          color: #909399;
        }

        .duration-tag {
          display: inline-flex;
          align-items: center;
          padding: 2px 10px;
          border-radius: 12px;
          font-size: 12px;
          font-weight: 600;
          color: #409EFF;
          background: rgba(64, 158, 255, 0.12);
          border: 1px solid rgba(64, 158, 255, 0.35);
        }
        
        .question-link {
          color: #409EFF;
          text-decoration: none;
          
          &:hover {
            text-decoration: underline;
          }
        }
      }
    }
    
    .detail-section {
      margin-top: 20px;
      
      h4 {
        font-size: 16px;
        color: #303133;
        margin: 0 0 15px 0;
        font-weight: 600;
        border-left: 3px solid #409EFF;
        padding-left: 10px;
      }
      
      .plan-info-content {
        background-color: #f9fafc;
        border-radius: 8px;
        padding: 15px;
        border: 1px solid #ebeef5;
        font-size: 14px; /* 统一与PC端一致 */
        
        /* Markdown 基础样式 */
        &.markdown-body {
          h1, h2, h3, h4, h5, h6 { margin: 10px 0 6px; font-weight: 600; }
          h1 { font-size: 20px; }
          h2 { font-size: 18px; }
          h3 { font-size: 16px; }
          p { margin: 6px 0; }
          ul, ol { padding-left: 25px !important; margin: 6px 0; }
          li { margin: 4px 0; }
          
          /* 任务列表样式（弹窗内） */
          ul.task-list {
            list-style: none !important;
            padding-left: 10px !important;
            margin: 6px 0;
          }
          ul.task-list li.task-list-item {
            list-style: none !important;
            display: flex;
            align-items: center;
          }
          ul.task-list li.task-list-item::marker { content: '' !important; }
          ul.task-list li.task-list-item::before { display: none !important; }
          ul.task-list li.task-list-item input[type="checkbox"] {
            margin: 0 8px 0 0;
            flex-shrink: 0;
            transform: scale(1.15);
            vertical-align: middle;
            position: relative;
            top: -1px;
          }

          /* 兜底处理：凡是包含checkbox的ul当作任务列表 */
          ul:has(li input[type="checkbox"]) {
            list-style: none !important;
            padding-left: 10px !important;
          }
          ul:has(li input[type="checkbox"]) li::marker { content: '' !important; }
          
          /* 再次增强：容器级别强制去除 marker (scoped 下使用 :deep 渗透到 v-html) */
          :deep(.plan-info-content.markdown-body ul.task-list),
          :deep(.plan-info-content.markdown-body ul:has(li input[type="checkbox"])) {
            list-style: none !important;
            padding-left: 10px !important;
          }
          :deep(.plan-info-content.markdown-body ul li.task-list-item),
          :deep(.plan-info-content.markdown-body ul li:has(input[type="checkbox"])) {
            list-style: none !important;
            display: flex;
            align-items: center;
          }
          :deep(.plan-info-content.markdown-body ul li.task-list-item::before),
          :deep(.plan-info-content.markdown-body ul li:has(input[type="checkbox"])::before) { display: none !important; }
          :deep(.plan-info-content.markdown-body ul li.task-list-item::marker),
          :deep(.plan-info-content.markdown-body ul li:has(input[type="checkbox"])::marker) { content: '' !important; }

          /* 有主题用 p::before 画点，任务列表需额外移除 */
          :deep(.plan-info-content.markdown-body ul li.task-list-item > p::before),
          :deep(.plan-info-content.markdown-body ul li:has(input[type="checkbox"]) > p::before) {
            content: none !important;
            display: none !important;
          }

          /* 统一有序/无序/任务列表的行间距 */
          :deep(.plan-info-content.markdown-body ul > li),
          :deep(.plan-info-content.markdown-body ol > li) {
            margin-top: 5px !important;
            margin-bottom: 5px !important;
          }
          /* 列表项里的段落默认有 margin，重置为 0，避免撑开行距 */
          :deep(.plan-info-content.markdown-body ul > li > p),
          :deep(.plan-info-content.markdown-body ol > li > p) {
            margin: 0 !important;
          }

          /* 兜底：任何任务项都强制没有 marker */
          :deep(.plan-info-content.markdown-body ul li.task-list-item) {
            list-style: none !important;
          }
          :deep(.plan-info-content.markdown-body ul li.task-list-item::marker) {
            content: '' !important;
          }
          
          /* 增强表格样式 */
          table { 
            border-collapse: collapse; 
            width: 100%; 
            margin: 6px 0; 
            display: table;
            border-spacing: 0;
            border-color: #e5e7eb;
          }
          thead { display: table-header-group; }
          tbody { display: table-row-group; }
          tr { display: table-row; }
          th, td { 
            border: 1px solid #e5e7eb; 
            padding: 6px 8px; 
            text-align: left; 
            display: table-cell;
            vertical-align: top;
          }
          thead th { 
            background: #f3f4f6; 
            font-weight: 600; 
            border-bottom: 2px solid #e5e7eb;
          }
          tbody tr:nth-child(even) { background-color: #f9fafb; }
          tbody tr:hover { background-color: #f3f4f6; }
          
          /* 专门为markdown-table类添加样式 */
          .markdown-table {
            border-collapse: collapse !important;
            width: 100% !important;
            margin: 6px 0 !important;
            display: table !important;
            border-spacing: 0 !important;
            border: 1px solid #e5e7eb !important;
          }
          .markdown-table thead { display: table-header-group !important; }
          .markdown-table tbody { display: table-row-group !important; }
          .markdown-table tr { display: table-row !important; }
          .markdown-table th, .markdown-table td { 
            border: 1px solid #e5e7eb !important; 
            padding: 6px 8px !important; 
            text-align: left !important; 
            display: table-cell !important;
            vertical-align: top !important;
          }
          .markdown-table thead th { 
            background: #f3f4f6 !important; 
            font-weight: 600 !important; 
            border-bottom: 2px solid #e5e7eb !important;
          }
          .markdown-table tbody tr:nth-child(even) { background-color: #f9fafb !important; }
          .markdown-table tbody tr:hover { background-color: #f3f4f6 !important; }

          code { background: #f3f4f6; padding: 2px 6px; border-radius: 4px; font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace; }
          pre { background: #1f2937; color: #e5e7eb; padding: 10px 12px; border-radius: 6px; overflow: auto; }
          blockquote { margin: 6px 0; padding: 6px 10px; border-left: 4px solid #dcdfe6; background: #ffffff; color: #606266; }
          a { color: #409EFF; text-decoration: none; }
          a:hover { text-decoration: underline; }
        }
      }
    }
  }
  
  .detail-actions {
    display: flex;
    justify-content: center;
    gap: 12px;

    /* icon-only action buttons to match swipe overlay */
    .action-icon-btn {
      width: 36px !important;
      height: 36px !important;
      min-width: 36px !important;
      padding: 0 !important;
      border-radius: 8px !important;
      background-color: #F2F3F5 !important;
      border: none !important;
      display: inline-flex !important;
      align-items: center !important;
      justify-content: center !important;
      margin: 0 !important;
    }
    .action-icon-btn i {
      font-size: 18px !important;
    }
    .action-icon-btn:hover {
      background-color: #E5E6EB !important;
    }
    .action-icon-btn:disabled {
      background-color: #F2F3F5 !important;
      opacity: 1 !important;
    }
    .action-icon-btn:disabled i {
      color: #C0C4CC !important;
    }

    /* color mapping per PC */
    .action-icon-btn i.el-icon-video-play { color: #67C23A; }
    .action-icon-btn i.el-icon-video-pause { color: #E6A23C; }
    .action-icon-btn i.el-icon-check { color: #67C23A; }
    .action-icon-btn i.el-icon-circle-close { color: #F56C6C; }
  }
}

/* 所有对话框共有样式 */
.dialog-container {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 2000;
  display: flex;
  justify-content: center;
}

.dialog-mask {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.7);
  z-index: 2001;
}

.custom-dialog {
  position: absolute;
  z-index: 2002;
  width: 95%;
  height: 80vh;
  background-color: #ffffff;
  border-radius: 8px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.3);
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

/* 计划详情弹窗：自适应高度且设定最小高度，内容超出时滚动 */
.custom-dialog.detail-dialog {
  height: auto;
  max-height: 80vh;
  min-height: 40vh; /* 减小最小高度，避免内容极少时空白过大 */
  display: flex;
  flex-direction: column;
}

/* 让详情对话框的主体区域填满容器，内部再拆分为滚动内容 + 固定操作区 */
.custom-dialog.detail-dialog .dialog-body {
  flex: 1;
  height: 100%;
  max-height: 100%;
  overflow: hidden; /* 由内部滚动容器接管滚动 */
  padding: 0; /* 由内部各自控制间距 */
  display: flex;
  flex-direction: column;
}

/* 统计弹窗：自适应高度，不占用固定80vh */
.custom-dialog.stats-dialog {
  height: auto;
  max-height: none;
  width: 92%;
  max-width: 520px;
}
.custom-dialog.stats-dialog .dialog-body {
  flex: initial;
  max-height: none;
  overflow-y: visible;
  padding: 12px 10px;
}

.dialog-close {
  position: absolute;
  top: 10px;
  right: 10px;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.1);
  color: #909399;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  cursor: pointer;
  z-index: 10;
}

.dialog-body {
  flex: 1;
  padding: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  
  &.filter-dialog-body {
    padding: 15px 15px 10px;
  }
}

/* 移动端确认弹窗优化（全局） - 放在全局样式块中生效 */

/* 查询弹窗专用样式 */
.filter-dialog-content {
  padding: 0;
  
  .filter-title {
    font-size: 16px;
    margin: 0 0 10px 0;
    padding-bottom: 8px;
  }
}

/* 查询弹窗：设置合理的高度限制 */
.custom-dialog:not(.detail-dialog):not(.stats-dialog) {
  height: auto;
  max-height: 70vh;
  min-height: 300px;
}

.custom-dialog:not(.detail-dialog):not(.stats-dialog) .dialog-body {
  max-height: calc(70vh - 60px); /* 减去关闭按钮和标题的高度 */
  overflow-y: auto;
}

.custom-dialog:not(.detail-dialog):not(.stats-dialog) .filter-dialog-body {
  max-height: calc(70vh - 60px);
  overflow-y: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;
  
  &::-webkit-scrollbar {
    display: none;
  }
}

.filter-form {
  .el-form-item {
    margin-bottom: 10px;
    
    .el-form-item__label {
      padding-right: 5px;
      line-height: 32px;
    }
    
    .el-form-item__content {
      line-height: 32px;
    }
  }
  
  ::v-deep .el-input__inner,
  ::v-deep .el-select .el-input__inner,
  ::v-deep .el-date-editor--daterange.el-input__inner {
    height: 32px;
    line-height: 32px;
  }
  
  ::v-deep .el-range-separator {
    line-height: 24px;
  }
  
  ::v-deep .el-range-input {
    height: 30px;
  }
}

.filter-footer {
  margin-top: 10px;
  padding-top: 8px;
  
  .el-button {
    padding: 7px 15px;
  }
}

/* 滚动容器样式 */
.detail-scroll-container {
  max-height: calc(80vh - 40px);
  overflow-y: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;
  
  &::-webkit-scrollbar {
    display: none;
  }
}

/* 日期选择器优化样式 */
.date-range-container {
  display: flex;
  align-items: center;
  gap: 2px;
  width: 100%;

  .date-picker-item {
    flex: 1;
    
    ::v-deep .el-input__inner {
      font-size: 12px !important;
      padding-left: 25px !important;
      padding-right: 5px !important;
    }
    
    ::v-deep .el-input__prefix {
      left: 3px !important;
    }
    
    ::v-deep .el-input__icon {
      width: 20px !important;
    }
  }
}

/* 对话框内容样式 */
.dialog-content {
  padding: 5px;
  background-color: #ffffff !important;
  
  &.no-title {
    padding-top: 0;
  }
  
  .dialog-custom-title {
    font-size: 18px;
    font-weight: 600;
    color: #303133;
    margin: 0 0 15px 0;
    padding-bottom: 10px;
    border-bottom: 1px solid #ebeef5;
    text-align: center;
  }
  
  .dialog-footer {
    text-align: right;
    margin-top: 15px;
    padding-top: 10px;
    border-top: 1px solid #ebeef5;
  }
}
</style>

<!-- 全局样式：专门用于覆盖Element UI弹窗 -->
<style>
/* 全局样式 - 不使用scoped，以便能够影响body下的元素 */
.el-picker-panel {
  left: 50% !important;
  transform: translateX(-50%) !important;
  margin-left: 0 !important;
  margin-right: 0 !important;
  z-index: 3000 !important;
}

/* 隐藏日期选择器箭头 */
.el-picker-panel .popper__arrow {
  display: none !important;
}

/* 优化日期选择器内容区域的padding，增加可视区域 */
.el-picker-panel .el-date-picker__body {
  padding: 10px 8px !important;
}

/* 优化日期表格单元格间距 */
.el-picker-panel .el-date-table th,
.el-picker-panel .el-date-table td {
  padding: 2px !important;
  width: 30px !important;
  height: 30px !important;
}

/* 优化日期选择器头部间距 */
.el-picker-panel .el-date-picker__header {
  margin: 6px 0 !important;
  padding: 0 8px !important;
}

/* 减少日期输入框的内部padding */
.date-picker-item .el-input__inner {
  padding-right: 25px !important;
  font-size: 13px !important;
}

/* 确保月份和年份选择下拉菜单不会被截断 */
.el-picker-panel .el-date-picker__header-label {
  font-size: 14px !important;
  padding: 0 2px !important;
}

/* 全局 - 移动端确认弹窗优化 */
.mobile-confirm-dialog {
  width: 88% !important;
  max-width: 420px !important;
  border-radius: 12px !important;
}
.mobile-confirm-dialog .el-message-box__header {
  padding: 12px 16px !important;
}
.mobile-confirm-dialog .el-message-box__title {
  font-size: 18px !important;
}
.mobile-confirm-dialog .el-message-box__content {
  padding: 12px 16px 0 16px !important;
  font-size: 15px !important;
  line-height: 1.6 !important;
}
.mobile-confirm-dialog .el-message-box__btns {
  padding: 12px 16px 16px !important;
}
.mobile-confirm-dialog .el-button + .el-button {
  margin-left: 12px !important;
}
.mobile-confirm-dialog .el-message-box__status { display: none !important; }
.mobile-confirm-dialog .el-button { padding: 10px 14px !important; }

/* 全局表格样式 - 确保markdown表格正确渲染 */
.markdown-table,
.plan-info-content table,
.markdown-body table {
  border-collapse: collapse !important;
  width: 100% !important;
  margin: 8px 0 !important;
  display: table !important;
  border-spacing: 0 !important;
  border: 1px solid #e5e7eb !important;
  table-layout: auto !important;
}

.markdown-table thead,
.plan-info-content table thead,
.markdown-body table thead {
  display: table-header-group !important;
}

.markdown-table tbody,
.plan-info-content table tbody,
.markdown-body table tbody {
  display: table-row-group !important;
}

.markdown-table tr,
.plan-info-content table tr,
.markdown-body table tr {
  display: table-row !important;
}

.markdown-table th,
.markdown-table td,
.plan-info-content table th,
.plan-info-content table td,
.markdown-body table th,
.markdown-body table td {
  border: 1px solid #e5e7eb !important;
  padding: 8px 12px !important;
  text-align: left !important;
  display: table-cell !important;
  vertical-align: top !important;
  word-wrap: break-word !important;
}

.markdown-table thead th,
.plan-info-content table thead th,
.markdown-body table thead th {
  background: #f3f4f6 !important;
  font-weight: 600 !important;
  border-bottom: 2px solid #e5e7eb !important;
}

.markdown-table tbody tr:nth-child(even),
.plan-info-content table tbody tr:nth-child(even),
.markdown-body table tbody tr:nth-child(even) {
  background-color: #f9fafb !important;
}

.markdown-table tbody tr:hover,
.plan-info-content table tbody tr:hover,
.markdown-body table tbody tr:hover {
  background-color: #f3f4f6 !important;
}
</style> 

<style>
/* 列表统一行距（全局，提升优先级，作用于 v-html 渲染内容） */
.plan-info-content.markdown-body ul > li,
.plan-info-content.markdown-body ol > li {
  margin-top: 5px !important;
  margin-bottom: 5px !important;
}
/* 段落重置，避免撑开 */
.plan-info-content.markdown-body ul > li > p,
.plan-info-content.markdown-body ol > li > p {
  margin: 0 !important;
}

/* 强制移除任务列表的 marker（提升优先级，覆盖主题） */
.plan-info-content.markdown-body ul.task-list,
.plan-info-content.markdown-body ul.task-list > li.task-list-item {
  list-style: none !important;
  list-style-type: none !important;
}
.plan-info-content.markdown-body ul.task-list > li.task-list-item::marker {
  content: '' !important;
}

/* 统一左侧缩进（高优先级作用于 v-html 内容） */
.plan-info-content.markdown-body ul,
.plan-info-content.markdown-body ol {
  padding-left: 25px !important;
}
.plan-info-content.markdown-body ul.task-list,
.plan-info-content.markdown-body ul:has(li input[type="checkbox"]) {
  padding-left: 10px !important;
}

/* 去除任务列表下的二级无序列表小点 */
.plan-info-content.markdown-body ul.task-list ul,
.plan-info-content.markdown-body ul:has(li input[type="checkbox"]) ul {
  list-style: none !important;
}
.plan-info-content.markdown-body ul.task-list ul > li::marker,
.plan-info-content.markdown-body ul:has(li input[type="checkbox"]) ul > li::marker {
  content: '' !important;
}
.plan-info-content.markdown-body ul.task-list ul > li > p::before,
.plan-info-content.markdown-body ul:has(li input[type="checkbox"]) ul > li > p::before {
  content: none !important;
  display: none !important;
}
</style>